import FontAwesomeProIcon from '@ui/components/FontAwesomeProIcon/FontAwesomeProIcon';
import styles from '@ui/components/FileUpload/FileUpload.module.scss';
import Card from '@ui/components/Card/Card';
import Content from '@ui/components/Content/Content';
import Button from '@ui/components/Button/Button';
import FileList, {
  getFileSize,
} from '@ui/components/FileUpload/components/FileList';
import CardBody from '@ui/components/Card/components/CardBody/CardBody';
import { FormLabel, FormInput } from '@ariakit/react';
import PropTypes from 'prop-types';
import React, {
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';

function fileName(type) {
  switch (type) {
    case '.doc':
    case '.docx':
    case '.docx,.doc':
      return 'Word';
    case '.xls':
    case '.xlsx':
    case '.xls,.xlsx':
    case '.xltx':
      return 'Excel';
    case '.tiff':
      return 'TIFF';
    case '.png':
      return 'PNG';
    case '.jpg':
    case '.jpeg':
      return 'JPEG';
    case '.pdf':
      return 'PDF';
    case '.twg':
      return 'TWG';
    default:
      return '';
  }
}

const getMaxUploadSizeReached = (files, maxUploadSize) => {
  const fileSizeSum = files?.reduce((sum, file) => sum + file.size, 0) ?? 0;
  return fileSizeSum >= maxUploadSize;
};

const getAllowedMimeTypes = extension => {
  const allowedMimeTypes = {
    doc: ['application/msword', 'application/vnd.ms-word'],
    docx: [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    jpg: ['image/jpeg'],
    jpeg: ['image/jpeg'],
    pdf: ['application/pdf'],
    png: ['image/png'],
    tiff: ['image/tiff', 'image/x-tiff'],
    xls: [
      'application/vnd.ms-excel',
      'application/msexcel',
      'application/x-msexcel',
      'application/x-ms-excel',
      'application/x-excel',
      'application/x-dos_ms_excel',
      'application/xls',
      'application/x-xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
    xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
    xltx: [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
    ],
  };

  if (Object.prototype.hasOwnProperty.call(allowedMimeTypes, extension)) {
    return allowedMimeTypes[extension];
  }

  return null;
};

const isAllowedFiletype = (filename, accept) =>
  accept.includes(`.${filename.toLowerCase().split('.').pop()}`);

const WrapperElement = ({ variant, ...props }) => {
  switch (variant) {
    case 'card':
      return (
        <Card>
          <CardBody {...props} />
        </Card>
      );
    case 'transparent':
      return <div {...props} />;
    default:
      return null;
  }
};

const FileUpload = ({
  store,
  multiple = true,
  heading = 'Dateien Hochladen',
  description = 'Sie haben hier die Möglichkeit noch weiterführende Dateien wie z.B. Baupläne hochzuladen.',
  accept,
  maxFileCount = 5,
  maxFileCountError = 'MAXIMALE DATEIMENGE ERREICHT',
  maxFileCountOverflowError = 'Es wurden nicht alle Dateien hinzugefügt, da das Limit erreicht wurde',
  maxUploadSize = 10000000,
  maxUploadSizeError = 'Die Dateien würden die maximal Größe übersteigen.',
  acceptedFileTypeError = 'Dieser Datei-Typ ist nicht erlaubt',
  onFileChange: defaultOnFileChange,
  name,
  required = false,
  variant = 'card',
}) => {
  const { touched, errors } = store.useState();
  const [files, setFiles] = useState(null);
  const [error, setError] = useState(null);
  const fileUploadRef = useRef(null);

  const onFileChange = useMemo(
    () => defaultOnFileChange || function () {},
    [defaultOnFileChange],
  );
  const maxFileCountReached = useMemo(
    () => (files?.length ?? 0) >= maxFileCount,
    [files, maxFileCount],
  );
  const maxUploadSizeReached = useMemo(getMaxUploadSizeReached, [
    files,
    maxUploadSize,
  ]);
  const buttonDisabled = useMemo(
    () => maxFileCountReached || maxUploadSizeReached,
    [maxFileCountReached, maxUploadSizeReached],
  );

  const handleFileUploadClick = () => {
    if (!fileUploadRef.current) {
      return;
    }

    fileUploadRef.current.click();
  };

  const handleFileChange = e => {
    setError(null);

    const uploadedFiles = Array.from(e.target.files);

    // Reset file input, so if the user uploads a file deletes it and
    // re-uploads it, we will get a change event on the file input.
    e.target.value = '';

    if (uploadedFiles.length <= 0) {
      return;
    }

    const maxFileReached =
      (files?.length ?? 0) + uploadedFiles.length > maxFileCount;

    // remove files that exceed limit
    if (maxFileReached) {
      const remove = Math.abs(
        maxFileCount - ((files?.length ?? 0) + uploadedFiles.length),
      );
      uploadedFiles.splice(uploadedFiles.length - 1 - remove, remove);
      setError(maxFileCountOverflowError);
    }

    const maxSizeReached = files
      ? getMaxUploadSizeReached([...files, ...uploadedFiles], maxUploadSize)
      : getMaxUploadSizeReached(uploadedFiles, maxUploadSize);

    if (maxSizeReached) {
      setError(maxUploadSizeError);
      return;
    }

    const isAllowed = uploadedFiles.every(file => {
      const extension = file.name.split('.').pop();
      const fileMimeTypes = getAllowedMimeTypes(extension);

      return fileMimeTypes && fileMimeTypes.includes(file.type);
    });

    if (!isAllowed) {
      setError(acceptedFileTypeError);
    }

    const hasEmptyFiles = uploadedFiles.find(file => file.size <= 0);

    if (hasEmptyFiles) {
      setError(
        'Dateien die weniger als 1 Byte groß sind können nicht hochgeladen werden!',
      );
    }

    const finalFiles = uploadedFiles.filter(
      file => file.size > 0 && isAllowedFiletype(file.name, accept),
    );

    setFiles(prev => (prev ? [...prev, ...finalFiles] : finalFiles));
  };

  const handleFileRemove = useCallback(
    index => {
      setFiles(prevFiles => prevFiles?.filter((_, i) => i !== index));
    },
    [setFiles, files],
  );

  useEffect(() => {
    onFileChange(files);
  }, [files]);

  if (!store || !name) {
    return `You need to provide the props store and name`;
  }

  return (
    <WrapperElement variant={variant}>
      <FontAwesomeProIcon
        className={styles.headingIcon}
        icon="file-alt"
        role="presentation"
        type="regular"
      />
      <FormLabel className={styles.heading} name={name} store={store}>
        {heading}
      </FormLabel>
      <Content noSpacing withParser>
        {description}
      </Content>
      <Content>
        {accept
          ? [...new Set(accept.map(fileName))].join(', ')
          : 'Alle Dateien'}
      </Content>

      <FileList files={files} onFileRemove={handleFileRemove} />

      <FormInput
        accept={accept}
        className={styles.inputFile}
        multiple={multiple}
        name={name}
        onChange={handleFileChange}
        ref={fileUploadRef}
        required={required}
        store={store}
        type="file"
        value=""
      />
      <Button
        className={styles.uploadButton}
        disabled={buttonDisabled}
        onClick={handleFileUploadClick}
        variant="link"
      >
        <FontAwesomeProIcon
          className={styles.uploadIcon}
          icon="cloud-upload-alt"
        />
        {buttonDisabled
          ? maxFileCountError
          : `DOKUMENT HOCHLADEN (INSGESAMT MAX. ${getFileSize(maxUploadSize)})`}
      </Button>
      {(error || (touched[name] && errors[name])) && (
        <div aria-atomic="true" className={styles.errorMessage} role="alert">
          {error ?? errors[name]}
        </div>
      )}
    </WrapperElement>
  );
};

FileUpload.propTypes = {
  acceptedFileTypeError: PropTypes.string,
  description: PropTypes.string,

  heading: PropTypes.string,
  maxFileCount: PropTypes.number,
  maxFileCountError: PropTypes.string,
  maxFileCountOverflowError: PropTypes.string,
  maxUploadSize: PropTypes.number,
  maxUploadSizeError: PropTypes.string,
  multiple: PropTypes.bool,
  name: PropTypes.string.isRequired,
  onFileChange: PropTypes.func,
  required: PropTypes.bool,
  /* eslint-disable react/forbid-prop-types */
  /** `useFormStore()` return value from `@ariakit/react` */
  store: PropTypes.object.isRequired,
  /* eslint-enable react/forbid-prop-types */
  variant: PropTypes.oneOf(['transparent', 'card']),
};

export default FileUpload;
